import { forwardRef, useEffect, useImperativeHandle } from "react";
import type { DefaultValues, Resolver } from "react-hook-form";
import { useForm, useWatch } from "react-hook-form";

import { EmailInput, MobileNumberInput, RadioInput, TextInput } from "@/shared/components";
import CollapsibleField from "@/shared/components/CollapsibleField";
import { LOCALISATION_CONFIG } from "@/shared/config/localisation";
import type { StepFormComponentProps, StepFormHandle } from "@/shared/stepper/types";
import { formatVehicleNumber } from "@/shared/utils/indianVehicleNumber";
import { WHITE_LABEL_CONFIG } from "@/whiteLable";
import { usePosterReducers } from "../redux/store/useGetData";

export type SharedPersonalDetailsValues = {
  firstName: string;
  lastName: string;
  mobileNumber: {
    countryCode: string;
    countryCodeIso: string;
    number: string;
  };
  email: string;
  partnerCode: string;
  friendsAndFamilyContact: string;
  marketingConsent: "yes" | "no";
  vehicleRegistrationNumber?: string;
  startDate?: string;
  endDate?: string;
  start?: string;
  to?: string;
};

export type SharedPersonalAutofillValues = Partial<
  Omit<SharedPersonalDetailsValues, "mobileNumber">
> & {
  mobileNumber?: Partial<SharedPersonalDetailsValues["mobileNumber"]>;
};

type PersonalDetailsFormStepProps = StepFormComponentProps<
  SharedPersonalDetailsValues,
  {
    resolver: Resolver<SharedPersonalDetailsValues>;
    fallbackValues: SharedPersonalDetailsValues;
    autofillValues?: SharedPersonalAutofillValues;
    autofillVersion?: number;
    layoutClassName?: string;
    showAdditionalFields?: boolean;
    onValuesChange?: (values: SharedPersonalDetailsValues) => void;
    onDemoDataClick?: () => void;
    isReadOnly?: boolean;
    showVehicleRegistrationNumberField?: boolean;
  }
>;

function PersonalDetailsFormStepInner(
  {
    defaultValues,
    resolver,
    fallbackValues,
    autofillValues,
    autofillVersion,
    layoutClassName = "grid grid-cols-1 gap-4 sm:grid-cols-2",
    showAdditionalFields = false,
    onValuesChange,
    onDemoDataClick,
    isReadOnly = false,
    showVehicleRegistrationNumberField = false,
  }: PersonalDetailsFormStepProps,
  ref: React.ForwardedRef<StepFormHandle<SharedPersonalDetailsValues>>
) {
  const resolvedDefaultValues = (defaultValues ??
    fallbackValues) as DefaultValues<SharedPersonalDetailsValues>;

  const form = useForm<SharedPersonalDetailsValues>({
    resolver,
    defaultValues: resolvedDefaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const watchedValues = useWatch({
    control: form.control,
  }) as SharedPersonalDetailsValues;

  const {
    sharedPersonalDetails,
    auth: { customerAuth },
    mobileCountryCode: countryList = [],
  } = usePosterReducers();

  const LoginEmail = customerAuth?.client?.email;
  const LoginMobile = customerAuth?.client?.phone_number;
  const LoginCountryCode = customerAuth?.client?.country_code;

  const isLoggedIn = Boolean(customerAuth?.access_token);

  const handleDemoDataClick = () => {
    form.setValue(
      "firstName",
      sharedPersonalDetails?.firstName || customerAuth?.client?.first_name || "Demo",
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );

    form.setValue(
      "lastName",
      sharedPersonalDetails?.lastName || customerAuth?.client?.last_name || "User",
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );

    onDemoDataClick?.();
  };

  console.log(
    "Customer auth in PersonalDetailsFormStep:",
    LoginEmail,
    LoginMobile,
    LoginCountryCode,
    isLoggedIn
  );
  useEffect(() => {
    const resolvedCountryCode = (() => {
      const loginCode = String(LoginCountryCode || "").replace(/\D/g, "");
      const defaultCode = String(LOCALISATION_CONFIG.mobileCountryCode || "");
      const savedCode = String(resolvedDefaultValues.mobileNumber?.countryCode || "").replace(
        /\D/g,
        ""
      );
      const savedNumber = String(resolvedDefaultValues.mobileNumber?.number || "").trim();

      if (isLoggedIn) {
        return loginCode || defaultCode;
      }

      // If number is still empty, prefer env-based localisation default.
      if (!savedNumber) {
        return defaultCode;
      }

      return savedCode || defaultCode;
    })();

    const resolvedCountryIso =
      resolvedCountryCode === LOCALISATION_CONFIG.mobileCountryCode
        ? LOCALISATION_CONFIG.mobileCountryIso
        : resolvedDefaultValues.mobileNumber?.countryCodeIso;

    form.reset({
      ...resolvedDefaultValues,
      email: isLoggedIn ? LoginEmail || "" : resolvedDefaultValues.email,
      mobileNumber: {
        ...(resolvedDefaultValues.mobileNumber || {}),
        countryCode: resolvedCountryCode,
        countryCodeIso: resolvedCountryIso,
        number: isLoggedIn ? LoginMobile || "" : resolvedDefaultValues.mobileNumber?.number,
      },
    });
  }, [form, resolvedDefaultValues, isLoggedIn, LoginEmail, LoginMobile, LoginCountryCode]);
  useEffect(() => {
    if (!autofillVersion || !autofillValues) {
      return;
    }

    if (autofillValues.firstName !== undefined) {
      form.setValue("firstName", autofillValues.firstName, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (autofillValues.lastName !== undefined) {
      form.setValue("lastName", autofillValues.lastName, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (autofillValues.email !== undefined && !isLoggedIn) {
      form.setValue("email", autofillValues.email, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (autofillValues.mobileNumber?.countryCode !== undefined && !isLoggedIn) {
      form.setValue("mobileNumber.countryCode", autofillValues.mobileNumber.countryCode, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (autofillValues.mobileNumber?.number !== undefined && !isLoggedIn) {
      form.setValue("mobileNumber.number", autofillValues.mobileNumber.number, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [autofillValues, autofillVersion, form, isLoggedIn]);

  useEffect(() => {
    if (!onValuesChange) {
      return;
    }

    onValuesChange(watchedValues || form.getValues());
  }, [form, onValuesChange, watchedValues]);

  useEffect(() => {
    const countryCode = String(watchedValues?.mobileNumber?.countryCode || "").replace(/\D/g, "");
    const selectedCountry = countryList.find(
      (country) => String(country.mobile_country_code || "").replace(/\D/g, "") === countryCode
    );

    if (
      selectedCountry &&
      watchedValues?.mobileNumber?.countryCodeIso !== selectedCountry.country_code
    ) {
      form.setValue("mobileNumber.countryCodeIso", selectedCountry.country_code, {
        shouldDirty: false,
      });
    }
  }, [
    countryList,
    form,
    watchedValues?.mobileNumber?.countryCode,
    watchedValues?.mobileNumber?.countryCodeIso,
  ]);

  console.log("Autofill values in form:", autofillValues);

  useImperativeHandle(ref, () => ({
    submit: async () => form.trigger(undefined, { shouldFocus: true }),
    getValues: () => form.getValues(),
  }));

  return (
    <fieldset disabled={isReadOnly} className="space-y-5">
      <div className={layoutClassName}>
        <TextInput
          control={form.control}
          required
          name="firstName"
          label="First Name"
          placeholder="Enter first name"
        />
        <TextInput
          control={form.control}
          name="lastName"
          label="Last Name"
          required
          placeholder="Enter last name"
        />

        <MobileNumberInput
          control={form.control}
          name="mobileNumber"
          label="Mobile Number"
          required
          disabled={isLoggedIn}
        />
        <EmailInput
          control={form.control}
          name="email"
          label="Email"
          required
          // placeholder="name@example.com"
          disabled={isLoggedIn}
        />

        {showVehicleRegistrationNumberField ? (
          <TextInput
            control={form.control}
            name="vehicleRegistrationNumber"
            label="Vehicle Registration Number"
            placeholder="Enter vehicle number"
            description="Enter vehicle number and continue. We will auto-generate quotes based on vehicle details."
            maxLength={13}
            formatValueOnChange={formatVehicleNumber}
          />
        ) : null}
        {showAdditionalFields && (
          <>
            <CollapsibleField label="Enter Partner Code">
              <TextInput
                control={form.control}
                name="partnerCode"
                label=""
                placeholder="Enter partner code"
              />
            </CollapsibleField>

            <CollapsibleField label="Friends & Family Contact">
              <TextInput
                control={form.control}
                name="friendsAndFamilyContact"
                label=""
                placeholder="Enter contact"
              />
            </CollapsibleField>

            <RadioInput
              control={form.control}
              name="marketingConsent"
              label={WHITE_LABEL_CONFIG.journeys.marketingConsentLabel}
              options={[
                { label: "Yes", value: "yes" },
                { label: "No", value: "no" },
              ]}
            />
          </>
        )}
      </div>
    </fieldset>
  );
}

export const PersonalDetailsFormStep = forwardRef(PersonalDetailsFormStepInner) as (
  props: PersonalDetailsFormStepProps & {
    ref?: React.ForwardedRef<StepFormHandle<SharedPersonalDetailsValues>>;
  }
) => ReturnType<typeof PersonalDetailsFormStepInner>;
